import { supabase } from '../config/supabase';
import { validateLeafDesign } from '../geometry/leafDesign';
import { readDoorPattern } from '../geometry/doorPattern';
import { randomId } from '../utils/randomId';
import { assertStudioCloudWrite } from './studioLocalMode';

export const STUDIO_KEYS = {
  editor: ['hestia.leaf-editor.draft.v1', 'hestia.leaf-editor.designs.v1', 'hestia.studio.editor-settings.v1', 'hestia.editor.viewport-ao.v1', 'hestiaDoorCamV4', 'hestia.studio.chat.editor.v1'],
  batch: ['hestia.render-batch.v1', 'hestia.studio.batch-settings.v1', 'hestia.studio.batch-builder.v1', 'hestia.editor.viewport-ao.v1', 'hestia.studio.chat.batch.v1']
};
export function writeStudioCache(key, value) {
  const json = JSON.stringify(value);
  if (localStorage.getItem(key) === json) return;
  localStorage.setItem(key, json);
  for (const [section, keys] of Object.entries(STUDIO_KEYS)) if (keys.includes(key)) localStorage.setItem(`hestia.studio.dirty.${section}`, 'true');
  window.dispatchEvent(new CustomEvent('hestia:studio-change', { detail: key }));
}
export async function syncCachedStudioAssets(section, ownerId) {
  assertStudioCloudWrite();
  const { localEnvironments, readEnvironmentBlob } = await import('../geometry/environmentLibrary');
  const knownHDRs = new Set((await listStudioAssets('hdri')).map((row) => row.id));
  for (const item of await localEnvironments()) if (!knownHDRs.has(item.id)) await saveStudioAsset(item.id, 'hdri', await readEnvironmentBlob(item.id), item, ownerId);
  if (section === 'batch') {
    const knownRenders = new Set((await listStudioAssets('render')).map((row) => row.id));
    const { readRenderResult } = await import('../pages/dev/batchRenderStorage');
    for (const job of readStudioCache('hestia.render-batch.v1', { jobs: [] }).jobs) if (job.status === 'done' && !knownRenders.has(job.id)) {
      const result = await readRenderResult(job.id);
      if (result) await saveStudioAsset(job.id, 'render', result.blob, { filename: result.filename, ms: result.ms }, ownerId);
    }
  }
}
export function readStudioCache(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
export async function studioUser(required = false) {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (required && !data.session?.user) throw new Error('Sign in with your admin account to save to Supabase.');
  return data.session?.user;
}
export async function loadStudioWorkspace(section) {
  const user = await studioUser(true);
  const { data, error } = await supabase.from('door_studio_workspaces').select('payload,revision').eq('owner_id', user.id).eq('section', section).maybeSingle();
  if (error) throw error;
  return data;
}
export async function saveStudioWorkspace(section, revision, ownerId) {
  assertStudioCloudWrite();
  const user = await studioUser(true);
  if (ownerId && user.id !== ownerId) throw new Error('Account changed during save. Reload your workspace.');
  const payload = Object.fromEntries(STUDIO_KEYS[section].map((key) => [key, readStudioCache(key, null)]));
  const row = { owner_id: user.id, section, payload, revision: (revision || 0) + 1, updated_at: new Date().toISOString() };
  const query = revision ? supabase.from('door_studio_workspaces').update(row).eq('owner_id', user.id).eq('section', section).eq('revision', revision)
    : supabase.from('door_studio_workspaces').insert(row);
  const { data, error } = await query.select('revision').single();
  if (error) throw new Error(error.code === 'PGRST116' || error.code === '23505'
    ? 'This workspace changed on another device. Reload cloud data before saving again.' : error.message);
  return data.revision;
}
export async function saveStudioAsset(id, kind, blob, metadata, ownerId) {
  assertStudioCloudWrite();
  const user = await studioUser(); if (!user) return;
  if (ownerId && user.id !== ownerId) throw new Error('Account changed during asset upload.');
  const path = `${user.id}/${kind}/${id.replace(/[^a-zA-Z0-9-]/g, '_')}`;
  const { error: uploadError } = await supabase.storage.from('door-studio').upload(path, blob, { upsert: true, contentType: blob.type || 'application/octet-stream' });
  if (uploadError) throw uploadError;
  const { error } = await supabase.from('door_studio_assets').upsert({ owner_id: user.id, id, kind, path, metadata });
  if (error) throw error;
}
export async function listStudioAssets(kind) {
  const user = await studioUser(); if (!user) return [];
  const { data, error } = await supabase.from('door_studio_assets').select('id,kind,path,metadata').eq('owner_id', user.id).eq('kind', kind);
  if (error) throw error;
  return data;
}
export async function readStudioAsset(id) {
  const user = await studioUser(); if (!user) return null;
  const { data, error } = await supabase.from('door_studio_assets').select('path,metadata').eq('owner_id', user.id).eq('id', id).maybeSingle();
  if (error) throw error; if (!data) return null;
  const result = await supabase.storage.from('door-studio').download(data.path);
  if (result.error) throw result.error;
  return { blob: result.data, metadata: data.metadata };
}
export async function removeStudioAsset(id) {
  assertStudioCloudWrite();
  const user = await studioUser(); if (!user) return;
  const { data, error } = await supabase.from('door_studio_assets').select('path').eq('owner_id', user.id).eq('id', id).maybeSingle();
  if (error) throw error; if (!data) return;
  const removed = await supabase.storage.from('door-studio').remove([data.path]); if (removed.error) throw removed.error;
  const deleted = await supabase.from('door_studio_assets').delete().eq('owner_id', user.id).eq('id', id); if (deleted.error) throw deleted.error;
}
const DESIGN_COLUMNS = 'id,design,published,updated_at,deleted_at,allowed_families';
const designRow = row => ({ id: row.id, design: readDoorPattern(row.design), published: row.published,
  updatedAt: row.updated_at, deletedAt: row.deleted_at, allowedFamilies: row.allowed_families });
const designWriteError = error => new Error(error.code === 'PGRST116'
  ? 'This design changed or was deleted by another user. Select it again before saving.' : error.message);

export async function saveStudioDesign(design, id, { published, expectedUpdatedAt } = {}) {
  assertStudioCloudWrite();
  validateLeafDesign(design);
  const user = await studioUser(true);
  const record = { design: readDoorPattern(design), ...(typeof published === 'boolean' ? { published } : {}) };
  if (id && !expectedUpdatedAt) throw new Error('Select the shared design again before saving.');
  const query = id
    ? supabase.from('door_studio_designs').update(record).eq('id', id).is('deleted_at', null).eq('updated_at', expectedUpdatedAt)
    : supabase.from('door_studio_designs').insert({ ...record, id: randomId(), owner_id: user.id, published: published ?? false });
  const { data, error } = await query.select(DESIGN_COLUMNS).single();
  if (error) throw designWriteError(error);
  if (typeof published === 'boolean' && data.published !== published) throw new Error('The publication status was not saved. Reload the design and try again.');
  announceDesignChange();
  return designRow(data);
}
export async function deleteStudioDesign(id, expectedUpdatedAt) {
  assertStudioCloudWrite();
  await studioUser(true);
  const { data, error } = await supabase.from('door_studio_designs')
    .update({ deleted_at: new Date().toISOString(), published: false })
    .eq('id', id).eq('updated_at', expectedUpdatedAt).is('deleted_at', null).select(DESIGN_COLUMNS).single();
  if (error) throw designWriteError(error);
  announceDesignChange();
  return designRow(data);
}
export async function restoreStudioDesign(id, expectedUpdatedAt) {
  assertStudioCloudWrite();
  await studioUser(true);
  const { data, error } = await supabase.from('door_studio_designs')
    .update({ deleted_at: null, published: false }).eq('id', id).eq('updated_at', expectedUpdatedAt)
    .not('deleted_at', 'is', null).select(DESIGN_COLUMNS).single();
  if (error) throw designWriteError(error);
  announceDesignChange();
  return designRow(data);
}
export async function loadPublishedDesigns() {
  const { data, error } = await supabase.from('door_studio_designs').select('id,design,allowed_families').eq('published', true)
    .is('deleted_at', null).order('updated_at', { ascending: false });
  if (error) throw error;
  return data.flatMap(row => { try { return [{ id: row.id, design: readDoorPattern(row.design), allowedFamilies: row.allowed_families }]; } catch { return []; } });
}
export async function loadStudioDesigns() {
  await studioUser(true);
  // RLS allows the shared library (including trash) only to active Studio users.
  const { data, error } = await supabase.from('door_studio_designs').select(DESIGN_COLUMNS)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data.map(designRow);
}

export function announceDesignChange() {
  window.dispatchEvent(new Event('hestia:designs-updated'));
  try { localStorage.setItem('hestia.studio.designs-updated', String(Date.now())); } catch { /* storage optional */ }
}
export async function saveStudioDesignFamilies(changes) {
  assertStudioCloudWrite();
  await studioUser(true);
  const { data, error } = await supabase.rpc('save_studio_design_families', { p_changes: changes });
  if (error) throw error;
  announceDesignChange();
  return data.map(designRow);
}
